// src/pages/MedicamentoCatalogoPage.jsx

import { useState, useEffect, useMemo } from 'react';
import { toast } from 'react-toastify';
import axiosInstance from '../axiosInstance';
import {
  Box, Typography, Button, CircularProgress, Paper,
  IconButton, Dialog, DialogActions, DialogContent, DialogTitle, TextField,
  Stack, alpha, Tooltip, useMediaQuery, Chip
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';

import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MedicationIcon from '@mui/icons-material/Medication';
import CloseIcon from '@mui/icons-material/Close';

const MotionPaper = motion(Paper);

const isCancel = (e) =>
  e?.name === 'CanceledError' ||
  e?.name === 'AbortError' ||
  e?.code === 'ERR_CANCELED' ||
  e?.message === 'canceled';

const surfaceTonal = (theme) =>
  theme.palette.mode === 'light'
    ? alpha(theme.palette.primary.main, 0.06)
    : alpha(theme.palette.primary.light, 0.12);

const outlineSoft = (theme) =>
  theme.palette.mode === 'light'
    ? alpha(theme.palette.divider, 0.28)
    : alpha(theme.palette.divider, 0.45);

const hoverBg = (theme) =>
  theme.palette.mode === 'light'
    ? alpha(theme.palette.primary.main, 0.08)
    : alpha(theme.palette.primary.light, 0.16);

const pillTint = (theme) =>
  theme.palette.mode === 'light'
    ? alpha(theme.palette.primary.main, 0.06)
    : alpha(theme.palette.primary.light, 0.10);

const MedicamentoCatalogoPage = () => {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));

  const [medicamentos, setMedicamentos] = useState([]);
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  // UI: nombre_comercial mapea a notas_predeterminadas (API)
  const [currentMedicamento, setCurrentMedicamento] = useState({
    id: null,
    nombre: '',
    dosis_sugerida: '',
    nombre_comercial: ''
  });

  // Cargar
  const fetchMedicamentos = async (signal) => {
    setLoading(true);
    try {
      const res = await axiosInstance.get('/catalogo-medicamentos/', {
        signal,
        params: { t: Date.now() }
      });
      const data = res.data?.results || res.data || [];
      setMedicamentos(Array.isArray(data) ? data : []);
    } catch (e) {
      if (isCancel(e)) return;
      toast.error(e?.response?.data?.error || 'No se pudo cargar el catálogo de medicamentos.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const ctrl = new AbortController();
    fetchMedicamentos(ctrl.signal);
    return () => ctrl.abort();
  }, []);

  // Modal
  const handleOpen = (medicamento = null) => {
    if (medicamento) {
      setIsEditMode(true);
      setCurrentMedicamento({
        id: medicamento.id ?? medicamento.id_medicamento ?? null,
        nombre: medicamento.nombre || '',
        dosis_sugerida: medicamento.dosis_sugerida || '',
        nombre_comercial: medicamento.notas_predeterminadas || ''
      });
    } else {
      setIsEditMode(false);
      setCurrentMedicamento({ id: null, nombre: '', dosis_sugerida: '', nombre_comercial: '' });
    }
    setOpen(true);
  };

  const handleClose = () => {
    if (saving) return;
    setOpen(false);
  };

  // Guardar
  const handleSave = async () => {
    if (saving) return;
    const nombre = currentMedicamento.nombre.trim();
    const dosis = currentMedicamento.dosis_sugerida?.trim() || '';
    const nombreComercial = currentMedicamento.nombre_comercial?.trim() || '';

    if (!nombre) {
      toast.error('El nombre del medicamento es obligatorio.');
      return;
    }

    setSaving(true);
    const payload = {
      nombre,
      dosis_sugerida: dosis,
      notas_predeterminadas: nombreComercial // mapeo
    };

    try {
      if (isEditMode) {
        const id = currentMedicamento.id;
        if (!id) throw new Error('ID inválido para actualizar.');
        await axiosInstance.put(`/catalogo-medicamentos/${id}/`, payload);
        toast.success('Medicamento actualizado con éxito.');
      } else {
        await axiosInstance.post('/catalogo-medicamentos/', payload);
        toast.success('Medicamento creado con éxito.');
      }
      await fetchMedicamentos();
      setOpen(false);
    } catch (e) {
      const msg = e?.response?.data?.error
        || e?.response?.data?.detail
        || 'Hubo un error al guardar el medicamento.';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  // Eliminar
  const handleDelete = async (idRaw) => {
    const id = idRaw ?? null;
    if (!id) {
      toast.error('ID de medicamento no válido.');
      return;
    }
    if (!window.confirm('¿Estás seguro de que quieres eliminar este medicamento de tu catálogo?')) return;

    setDeletingId(id);
    try {
      await axiosInstance.delete(`/catalogo-medicamentos/${id}/`);
      toast.success('Medicamento eliminado.');
      await fetchMedicamentos();
    } catch (e) {
      const msg = e?.response?.data?.error
        || e?.response?.data?.detail
        || 'No se pudo eliminar el medicamento.';
      toast.error(msg);
    } finally {
      setDeletingId(null);
    }
  };

  const EmptyState = useMemo(() => (
    <Paper
      sx={{
        p: 3,
        textAlign: 'center',
        borderRadius: '28px',
        border: `1px solid ${outlineSoft(theme)}`,
        background: surfaceTonal(theme)
      }}
    >
      <Typography color="textSecondary" sx={{ fontSize: '1.05rem' }}>
        No hay medicamentos en tu catálogo. Añade uno nuevo para comenzar.
      </Typography>
    </Paper>
  ), [theme]);

  if (loading) {
    return <CircularProgress sx={{ display: 'block', margin: 'auto', mt: 4 }} />;
  }

  return (
    <Box pb={{ xs: 3, md: 4 }}>
      {/* HEADER animado tipo hero */}
      <MotionPaper
        initial={{ opacity: 0, y: -18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45 }}
        elevation={0}
        sx={{
          position: 'relative',
          overflow: 'hidden',
          borderRadius: 4,
          mb: 3,
          mx: { xs: 2, md: 3 },
          px: { xs: 3, md: 5 },
          py: { xs: 3, md: 4 },
          background: (t) =>
            `linear-gradient(135deg, ${t.palette.primary.main} 0%, ${t.palette.primary.dark} 100%)`,
          color: 'white',
        }}
      >
        {/* Círculos decorativos */}
        <Box
          sx={{
            position: 'absolute',
            top: -50,
            right: -50,
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: (t) => alpha('#fff', 0.12),
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -30,
            left: -30,
            width: 150,
            height: 150,
            borderRadius: '50%',
            background: (t) => alpha('#fff', 0.09),
          }}
        />

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" sx={{ position: 'relative', zIndex: 1 }}>
          <Box
            sx={{
              bgcolor: (t) => alpha('#fff', 0.2),
              color: 'white',
              borderRadius: '50%',
              width: 60,
              height: 60,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: (t) => `0 8px 32px ${alpha('#000', 0.2)}`,
              flexShrink: 0
            }}
          >
            <MedicationIcon sx={{ fontSize: 34 }} />
          </Box>

          <Box sx={{ flex: 1, textAlign: { xs: 'center', sm: 'left' } }}>
            <Typography variant="h4" sx={{ fontWeight: 800, textShadow: '0 2px 10px rgba(0,0,0,0.25)' }}>
              Mi Catálogo de Medicamentos
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.95 }}>
              Gestiona tus fármacos, dosis y nombre comercial en un solo lugar
            </Typography>
          </Box>

          <Tooltip title="Añadir un nuevo medicamento">
            <span>
              <Button
                variant="contained"
                startIcon={<AddCircleOutlineIcon />}
                onClick={() => handleOpen()}
                sx={{
                  bgcolor: 'white',
                  color: 'primary.main',
                  fontWeight: 'bold',
                  borderRadius: 3,
                  px: 3,
                  py: 1.2,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                  '&:hover': {
                    bgcolor: 'grey.100',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 25px rgba(0,0,0,0.2)'
                  },
                  transition: 'all 0.25s ease',
                }}
              >
                Añadir Medicamento
              </Button>
            </span>
          </Tooltip>
        </Stack>
      </MotionPaper>

      {/* Contenido principal con padding lateral coherente al hero */}
      <Box px={{ xs: 2, md: 3 }}>
        {/* Lista de pills full-width */}
        <Stack spacing={1.1}>
          {(!medicamentos || medicamentos.length === 0) ? (
            EmptyState
          ) : (
            medicamentos.map((med) => {
              const id = med.id ?? med.id_medicamento;
              const nombreComercial = med.notas_predeterminadas || '';

              return (
                <Paper
                  key={id}
                  variant="outlined"
                  sx={{
                    width: '100%',
                    p: { xs: 1.25, sm: 1.5 },
                    borderRadius: '999px',
                    borderColor: outlineSoft(theme),
                    background: `linear-gradient(0deg, ${pillTint(theme)}, ${pillTint(theme)})`,
                    display: 'flex',
                    alignItems: 'center',
                    gap: { xs: 1, sm: 1.5 },
                    transition: 'transform 0.15s ease, box-shadow 0.2s ease, background-color 0.2s ease',
                    boxShadow: theme.shadows[1],
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      boxShadow: theme.shadows[4],
                      backgroundColor: hoverBg(theme),
                    }
                  }}
                >
                  {/* Icono */}
                  <Box
                    sx={{
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 44,
                      height: 44,
                      borderRadius: '50%',
                      backgroundColor: surfaceTonal(theme),
                      color: 'primary.main',
                    }}
                    aria-hidden
                  >
                    <MedicationIcon fontSize="small" />
                  </Box>

                  {/* Texto principal */}
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 700 }}
                      noWrap
                      title={med.nombre}
                    >
                      {med.nombre}
                    </Typography>

                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={{ xs: 0.5, sm: 1.25 }}
                      alignItems={{ xs: 'flex-start', sm: 'center' }}
                      sx={{ mt: 0.25, minWidth: 0 }}
                    >
                      {/* Dosis */}
                      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
                        <Chip
                          size="small"
                          label="Dosis"
                          sx={{
                            borderRadius: '12px',
                            bgcolor: surfaceTonal(theme),
                            color: theme.palette.text.secondary,
                            fontWeight: 600,
                            height: 22
                          }}
                        />
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          noWrap
                          title={med.dosis_sugerida || 'N/A'}
                        >
                          {med.dosis_sugerida || 'N/A'}
                        </Typography>
                      </Stack>

                      {/* Separador sutil */}
                      <Box
                        sx={{
                          display: { xs: 'none', sm: 'block' },
                          width: 6,
                          height: 6,
                          borderRadius: '50%',
                          backgroundColor: outlineSoft(theme),
                          flexShrink: 0
                        }}
                      />

                      {/* Nombre Comercial */}
                      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0, flex: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                          Nombre Comercial:
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          noWrap
                          title={nombreComercial || 'N/A'}
                          sx={{ flex: 1, minWidth: 0 }}
                        >
                          {nombreComercial && nombreComercial.trim().length > 0 ? nombreComercial : 'N/A'}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Box>

                  {/* Acciones */}
                  <Box display="flex" alignItems="center" gap={0.5} sx={{ flexShrink: 0 }}>
                    <Tooltip title="Editar">
                      <IconButton
                        onClick={() => handleOpen({ ...med, id })}
                        color="primary"
                        size="small"
                        aria-label="Editar medicamento"
                        sx={{
                          borderRadius: '12px',
                          '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.12) }
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Eliminar">
                      <span>
                        <IconButton
                          onClick={() => handleDelete(id)}
                          color="error"
                          size="small"
                          aria-label="Eliminar medicamento"
                          disabled={deletingId === id}
                          sx={{
                            borderRadius: '12px',
                            '&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.12) },
                            opacity: deletingId === id ? 0.6 : 1
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </Paper>
              );
            })
          )}
        </Stack>
      </Box>

      {/* Modal Añadir/Editar */}
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        fullScreen={isSmDown}
        PaperProps={{
          sx: {
            borderRadius: isSmDown ? 0 : '28px',
            padding: isSmDown ? 0 : '8px',
            boxShadow: theme.shadows[5],
            background:
              theme.palette.mode === 'light'
                ? theme.palette.background.paper
                : alpha(theme.palette.background.paper, 0.98)
          }
        }}
      >
        <DialogTitle
          sx={{
            pb: isSmDown ? 0 : 1,
            pt: isSmDown ? 1 : 2,
            px: isSmDown ? 2 : 3,
            fontSize: isSmDown ? '1.25rem' : '1.5rem',
            fontWeight: 700
          }}
        >
          {isEditMode ? 'Editar Medicamento' : 'Añadir Nuevo Medicamento'}
          <IconButton
            onClick={handleClose}
            disabled={saving}
            sx={{
              position: 'absolute',
              right: 12,
              top: 12,
              color: 'text.secondary',
              borderRadius: '12px'
            }}
            aria-label="Cerrar"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: isSmDown ? 2 : 3, py: isSmDown ? 1 : 2 }}>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              autoFocus={!isSmDown}
              margin="dense"
              label="Nombre del Medicamento"
              fullWidth
              value={currentMedicamento.nombre}
              onChange={(e) => setCurrentMedicamento({ ...currentMedicamento, nombre: e.target.value })}
              required
              disabled={saving}
            />
            <TextField
              margin="dense"
              label="Dosis Sugerida (ej: 500 mg)"
              fullWidth
              value={currentMedicamento.dosis_sugerida}
              onChange={(e) => setCurrentMedicamento({ ...currentMedicamento, dosis_sugerida: e.target.value })}
              disabled={saving}
            />
            <TextField
              margin="dense"
              label="Nombre Comercial"
              fullWidth
              multiline
              rows={isSmDown ? 3 : 4}
              value={currentMedicamento.nombre_comercial}
              onChange={(e) => setCurrentMedicamento({ ...currentMedicamento, nombre_comercial: e.target.value })}
              disabled={saving}
            />
          </Stack>
        </DialogContent>

        {/* Acciones sticky en móvil */}
        {isSmDown ? (
          <DialogActions
            sx={{
              px: 2,
              py: 2,
              position: 'sticky',
              bottom: 0,
              backgroundColor: theme.palette.background.paper,
              borderTop: `1px solid ${outlineSoft(theme)}`,
              zIndex: 1,
              m: 0
            }}
          >
            <Stack direction="column" spacing={1} width="100%">
              <Button
                onClick={handleClose}
                variant="tonal"
                disabled={saving}
                fullWidth
                sx={{
                  borderRadius: '999px',
                  backgroundColor: surfaceTonal(theme),
                  color: theme.palette.primary.main
                }}
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSave}
                variant="contained"
                disabled={!currentMedicamento.nombre.trim() || saving}
                fullWidth
                sx={{ borderRadius: '999px' }}
              >
                {isEditMode ? (saving ? 'Guardando...' : 'Guardar Cambios') : (saving ? 'Creando...' : 'Crear')}
              </Button>
            </Stack>
          </DialogActions>
        ) : (
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button
              onClick={handleClose}
              variant="tonal"
              disabled={saving}
              sx={{
                borderRadius: '999px',
                backgroundColor: surfaceTonal(theme),
                color: theme.palette.primary.main
              }}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              disabled={!currentMedicamento.nombre.trim() || saving}
              sx={{ borderRadius: '999px' }}
            >
              {isEditMode ? (saving ? 'Guardando...' : 'Guardar Cambios') : (saving ? 'Creando...' : 'Crear')}
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </Box>
  );
};

export default MedicamentoCatalogoPage;
